import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useI18n } from "@/contexts/I18nContext";
import { PhoneIcon, WhatsAppIcon } from "@/components/Icons";
import { Edit } from "@/contexts/EditModeContext";

/* Anypaku custom marker — orange + navy pin */
const anypakuIcon = L.divIcon({
  className: "anypaku-marker",
  html: `
    <div style="position:relative; width:44px; height:56px;">
      <svg viewBox="0 0 44 56" width="44" height="56" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 2 C 11 2 2 11 2 22 C 2 36 22 54 22 54 C 22 54 42 36 42 22 C 42 11 33 2 22 2 Z"
              fill="#1A4B8C" stroke="#FFFFFF" stroke-width="2"/>
        <circle cx="22" cy="22" r="11" fill="#F58220"/>
        <text x="22" y="27" text-anchor="middle" font-family="Arial Black, sans-serif"
              font-size="14" font-weight="900" fill="#FFFFFF">A</text>
      </svg>
    </div>
  `,
  iconSize: [44, 56],
  iconAnchor: [22, 54],
  popupAnchor: [0, -50],
});

/* Jussaarenkuja 1B, Helsinki Laajasalo */
const ANYPAKU_LATLNG = [60.18335, 25.04885];

const MapSection = () => {
  const { t } = useI18n();

  /* Inject leaflet's default style fix once (transparent images path) */
  useEffect(() => {
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
      iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
      shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
    });
  }, []);

  const itineraryHref = `https://www.openstreetmap.org/directions?from=&to=${ANYPAKU_LATLNG[0]}%2C${ANYPAKU_LATLNG[1]}#map=15/${ANYPAKU_LATLNG[0]}/${ANYPAKU_LATLNG[1]}`;

  return (
    <section
      id="contact"
      className="px-6 md:px-12 lg:px-16 py-24"
      style={{ background: "var(--cream-2)", borderTop: "1px solid var(--line)" }}
      data-testid="map-section"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-3" style={{ color: "var(--orange)" }}>
            ★ <Edit path="map.open">{t.map.open}</Edit>
          </div>
          <h2
            className="display"
            style={{
              fontSize: "clamp(40px, 5vw, 76px)",
              color: "var(--navy)",
              lineHeight: 0.9,
            }}
          >
            <Edit path="map.heading">{t.map.heading}</Edit>
          </h2>
          <p
            className="mt-5 text-base md:text-lg max-w-md"
            style={{ color: "var(--mute)", lineHeight: 1.6 }}
          >
            <Edit path="map.sub">{t.map.sub}</Edit>
          </p>

          <div
            className="mt-8 rounded-2xl p-6 shadow-md"
            style={{
              background: "#FFFFFF",
              border: "1px solid #DCE3ED",
            }}
          >
            <div className="flex items-start gap-3">
              <div
                style={{
                  background: "var(--navy)",
                  color: "#FFFFFF",
                  width: 40,
                  height: 40,
                  borderRadius: 999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontWeight: 800,
                }}
              >
                A
              </div>
              <div>
                <div
                  className="font-bold text-lg"
                  style={{ color: "var(--navy)" }}
                >
                  <Edit path="map.name">{t.map.name}</Edit>
                </div>
                {t.map.tagline && (
                  <div
                    className="text-xs mt-0.5"
                    style={{ color: "var(--mute)" }}
                  >
                    <Edit path="map.tagline">{t.map.tagline}</Edit>
                  </div>
                )}
                <div
                  className="text-sm mt-1"
                  style={{ color: "var(--midnight)" }}
                >
                  <Edit path="map.address">{t.map.address}</Edit>
                </div>
                <div
                  className="text-xs mt-2"
                  style={{ color: "var(--mute)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em" }}
                >
                  60.18335° N · 25.04885° E
                </div>
              </div>
            </div>

            <div className="mt-5 space-y-2">
              <a
                href="tel:+358401683383"
                className="flex items-center justify-between px-4 py-3 rounded-xl"
                style={{ background: "var(--navy)", color: "#FFFFFF" }}
                data-testid="contact-phone"
              >
                <span className="font-bold inline-flex items-center gap-2">
                  <PhoneIcon size={16} /> +358 40 168 3383
                </span>
                <span style={{ opacity: 0.8 }}>→</span>
              </a>
              <a
                href="https://wa.me/358401683383"
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center justify-between px-4 py-3 rounded-xl"
                style={{ background: "var(--orange)", color: "#FFFFFF" }}
                data-testid="contact-whatsapp"
              >
                <span className="font-bold inline-flex items-center gap-2">
                  <WhatsAppIcon size={18} /> WhatsApp
                </span>
                <span style={{ opacity: 0.9 }}>→</span>
              </a>
              <a
                href={itineraryHref}
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center justify-between px-4 py-3 rounded-xl border-2"
                style={{ borderColor: "var(--navy)", color: "var(--navy)" }}
                data-testid="contact-directions"
              >
                <span className="font-bold">🗺 {t.map.itinerary}</span>
                <span>→</span>
              </a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div
            className="rounded-3xl overflow-hidden shadow-xl"
            style={{ border: "2px solid var(--navy)", height: 520 }}
          >
            <MapContainer
              center={ANYPAKU_LATLNG}
              zoom={14}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={false}
              data-testid="leaflet-map"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={ANYPAKU_LATLNG} icon={anypakuIcon}>
                <Popup>
                  <div style={{ fontFamily: "system-ui", lineHeight: 1.4 }}>
                    <strong style={{ color: "#1A4B8C" }}>{t.map.name}</strong><br />
                    {t.map.address}<br />
                    <span style={{ fontSize: 11, color: "#7B8AA1" }}>
                      {t.map.open}
                    </span>
                  </div>
                </Popup>
              </Marker>
            </MapContainer>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MapSection;
